import { Box, Container, Stack } from "@mui/material";
import { SSText } from "src/components/base/components/SSText";
import { useSSTheme } from "../../reusable/hooks/useSSTheme";
import { useCssMediaQuery } from "src/components/base/hooks/useCssMediaQuery";
import { LazyImage } from "src/components/base/components/LazyImage";
import React from "react";
import { TPass } from "src/shared/types/globalStotre.types";
import { useTeamConfig } from "src/components/reusable/components/TeamConfigProvider";
import {
  PassCard,
  PassGiftButton,
  PassSubscribeNowButton,
  TPassCardProps,
} from "./PassCard";
import { getPassCardPrice } from "./utils.purchaseFlow";

const BackgroundImage = (props: { src: string; alt: string }) => {
  const { md: mdUp } = useCssMediaQuery("up");
  const { LANDING_PAGE_SETTINGS } = useTeamConfig();

  const bannerHeight = LANDING_PAGE_SETTINGS?.PASS_BANNER
    ?.IS_FLUID_BACKGROUND_IMAGE_SIZE
    ? "fit-content"
    : mdUp
    ? "450px"
    : "550px";

  const desktopMaxHeight = mdUp
    ? LANDING_PAGE_SETTINGS?.PASS_BANNER?.DESKTOP_MAX_HEIGHT
    : undefined;

  return (
    <Box
      width={"100%"}
      height={desktopMaxHeight || bannerHeight}
      overflow={"hidden"}
      sx={{
        filter: !LANDING_PAGE_SETTINGS?.PASS_BANNER
          ?.DISABLE_BACKGROUND_IMAGE_FILTER
          ? "grayscale(1) brightness(0.3)"
          : "none",
      }}
    >
      <LazyImage
        alt={props.alt}
        src={props.src}
        width={2160}
        height={2700}
        style={{ objectFit: "cover", width: "100%", height: "100%" }}
      />
    </Box>
  );
};

const PassDetailsOverlayed = (props: { pass: TPass }) => {
  const { md: mdUp } = useCssMediaQuery("up");
  const theme = useSSTheme();
  const teamConfig = useTeamConfig();
  const { sm: smUp } = useCssMediaQuery("up");

  return (
    <Stack
      alignItems={"center"}
      justifyContent={"flex-end"}
      width={"100%"}
      height={"100%"}
      position={"absolute"}
    >
      <Container sx={{ display: "flex", justifyContent: "center" }}>
        <Stack
          py={4}
          spacing={3}
          maxWidth={smUp ? "600px" : "100%"}
          textAlign={"center"}
        >
          <Stack px={2} spacing={1}>
            <SSText
              variant="h1"
              color={theme.colors.teamSecondary}
              lineHeight={1.1}
              dangerouslySetInnerHTML={{ __html: props.pass.passTitle }}
            />
            <SSText variant="h2" color={theme.colors.background.white}>
              ${getPassCardPrice(props.pass, teamConfig)}
            </SSText>
            <SSText
              variant="body"
              color={theme.colors.gray[100]}
              dangerouslySetInnerHTML={{ __html: props.pass.passDescription }}
            />
          </Stack>

          <Stack
            direction={mdUp ? "row" : "column"}
            alignItems={"center"}
            spacing={2}
          >
            <PassSubscribeNowButton
              pass={props.pass}
              sx={{
                maxWidth: "350px",
                backgroundColor:
                  teamConfig.LANDING_PAGE_SETTINGS?.PASS
                    ?.SUBSCRIBE_BUTTON_COLOR ??
                  teamConfig.TEAM_GLOBAL_SETTINGS?.COLORS_PRIMARY,
              }}
            />

            <PassGiftButton
              pass={props.pass}
              variant="contained"
              colorScheme={theme.mui.palette.common.white}
            />
          </Stack>
        </Stack>
      </Container>
    </Stack>
  );
};

export interface TPassBannerProps {
  pass: TPass;
  hidePassDetails?: boolean;
  passDisplayMode?: "overlayed" | "separate";
  passCardProps?: Partial<TPassCardProps>;
}

export function PassBanner(props: TPassBannerProps) {
  const { sm: smUp } = useCssMediaQuery("up");

  return (
    <>
      <Stack position={"relative"}>
        {smUp && props?.pass?.passBackgroundUrl_lg ? (
          <BackgroundImage
            alt={props.pass.passTitle}
            src={props.pass.passBackgroundUrl_lg}
          />
        ) : null}

        {!smUp && props?.pass?.passBackgroundUrl_sm ? (
          <BackgroundImage
            alt={props.pass.passTitle}
            src={props.pass.passBackgroundUrl_sm}
          />
        ) : null}
        {!props?.hidePassDetails &&
        (!props?.passDisplayMode || props.passDisplayMode == "overlayed") ? (
          <PassDetailsOverlayed pass={props.pass} />
        ) : null}
      </Stack>
      {!props.hidePassDetails && props.passDisplayMode == "separate" ? (
        <Container maxWidth="lg">
          <Stack pt={smUp ? 4 : 3}>
            <PassCard
              variant="variant1"
              pass={props.pass}
              {...props.passCardProps}
            />
          </Stack>
        </Container>
      ) : null}
    </>
  );
}
